import { useContext } from 'react'
import PropTypes from 'prop-types'

import {
  Button,
  Columns,
  LoadingModule,
  SaveFormModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useService from '@hooks/useService'
import useServiceProduct, { useServiceProductForm } from '@hooks/useServiceProduct'

import EntityAssets from '@components/EntityAssets'
import LinkToEntityModule from '@components/LinkToEntityModule'

import ArtworkSettings from '../ArtworkSettings'
import AttachmentSettings from '../AttachmentSettings'
import Categories from '../Categories'
import Details from '../Details'
import DigitalSettings from '../DigitalSettings'
import PageLayout from '../../../../components/PageLayout'
import ProductSettings from '../ProductSettings'
import ProposalSettings from '../ProposalSettings'
import PublicationSettings from '../PublicationSettings'
import ServiceProductTools from '../../../../components/ServiceProductTools'
import SocialMedia from '../SocialMedia'
import TemplatePages from '../TemplatePages'

import CampaignField from '../CampaignFields'

const PageContent = (props) => {
  const { serviceProductId } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks,
    callbacks: {
      updateServiceProduct,
    },
  } = pageContext

  const serviceProductPayload = useServiceProduct({ id: serviceProductId })
  const {
    callbacks: {
      launchDuplicateServiceProductModal,
      manageServiceProductTaskOptions,
      updateServiceProduct: updateFn,
    },
    loading,
    service,
    serviceProduct,
    updating,
  } = serviceProductPayload

  const serviceProductForm = useServiceProductForm(serviceProduct)
  const { entityState, saveEnabled } = serviceProductForm

  const servicePayload = useService(service)
  const {
    isAttachable,
    isCategorizable,
    isForDigital,
    isForProposal,
    isForPublication,
    isForSocialMedia,
    isTemplatable,
    service: { title: serviceTitle, short_name: serviceShortName },
    urls: { servicesIndexUrl },
  } = servicePayload

  const updateServiceProductPayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: entityState,
    toastText: 'Product Updated',
  }

  return (
    <PageLayout entityPayload={serviceProductPayload} activeTabBarItemKey="details">
      <Columns.Main>
        <LoadingModule loading={loading} times={3} />

        {!loading && (
          <>
            <Details serviceProductForm={serviceProductForm} />

            {isTemplatable && <ArtworkSettings serviceProductForm={serviceProductForm} />}

            {isAttachable && <AttachmentSettings serviceProductForm={serviceProductForm} />}

            {isForDigital && <DigitalSettings serviceProductForm={serviceProductForm} />}

            {isForProposal && <ProposalSettings serviceProductForm={serviceProductForm} />}

            {isForSocialMedia && <SocialMedia serviceProductForm={serviceProductForm} />}

            {isForPublication && (
              <PublicationSettings serviceProductForm={serviceProductForm} />
            )}

            <SaveFormModule>
              <Button
                buttonStyle="primaryCreate"
                disabled={!saveEnabled}
                loading={updating}
                onClick={() => updateServiceProduct(updateServiceProductPayload)}
                size="medium"
                style={{ width: '100%' }}
              >
                Save Details
              </Button>
            </SaveFormModule>
          </>
        )}
      </Columns.Main>

      <Columns.Sidebar>
        <LinkToEntityModule
          entityTitle={serviceTitle}
          goToHref={servicesIndexUrl}
          loading={loading}
          moduleTitle="Service"
          secondaryText={serviceShortName}
        />

        <ServiceProductTools
          callbacks={{ launchDuplicateServiceProductModal, manageServiceProductTaskOptions }}
          service={service}
        />

        <CampaignField serviceProductPayload={serviceProductPayload} />

        <ProductSettings
          serviceProductPayload={serviceProductPayload}
          serviceProductForm={serviceProductForm}
        />

        {isCategorizable && <Categories serviceProductPayload={serviceProductPayload} />}

        {isTemplatable && <TemplatePages serviceProductPayload={serviceProductPayload} />}

        <EntityAssets
          callbacks={callbacks}
          filters={{ attachableId: serviceProductId, attachableType: 'ServiceProduct' }}
        />
      </Columns.Sidebar>
    </PageLayout>
  )
}

PageContent.propTypes = {
  serviceProductId: PropTypes.number.isRequired,
}

export default PageContent
